import { useEffect, useState } from 'react';

import { CompanySettings } from '~hooks/useCompanySettings/models';
import useCompanySettings from '~hooks/useCompanySettings/useCompanySettings';
import { useStores } from '~store';

// The settings of the company that owns a record, which can differ from the user's own.
// Null only until the first company resolves; while a later company loads, the previous
// company's settings stay in effect. A failed read turns every setting off.
export default function useRecordCompanySettings(companyId: string | undefined) {
  const { userStore } = useStores();
  const { getManagedCompanySettings } = useCompanySettings();
  const userCompanySettings = userStore.companySettings;
  const isUserCompany = !companyId || companyId === userStore.userCompany?.id;
  const [resolvedCompanySettings, setResolvedCompanySettings] =
    useState<CompanySettings | null>(null);

  useEffect(() => {
    if (isUserCompany || !companyId) {
      setResolvedCompanySettings(userCompanySettings);
      return;
    }

    const controller = new AbortController();

    const loadCompanySettings = async () => {
      try {
        const companySettings = await getManagedCompanySettings(
          companyId,
          controller.signal,
        );
        if (controller.signal.aborted) return;

        setResolvedCompanySettings(companySettings ?? new CompanySettings({}));
      } catch {
        if (controller.signal.aborted) return;

        setResolvedCompanySettings(new CompanySettings({}));
      }
    };

    void loadCompanySettings();

    return () => controller.abort();
  }, [companyId, getManagedCompanySettings, isUserCompany, userCompanySettings]);

  return isUserCompany ? userCompanySettings : resolvedCompanySettings;
}
